import cloneDeep from 'lodash/cloneDeep'
import React, { useState } from 'react'
import {
  LuCheck,
  LuCircleCheck,
  LuCircleEllipsis,
  LuCirclePlus,
  LuMail,
  LuSlash,
  LuTriangleAlert,
} from 'react-icons/lu'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ReferralsImportItem } from '@/redux/slices/referralsImportBase'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItem) => void
  isHistorical?: boolean
}

const InviteNewFromAdviserCell: React.FC<Props> = ({
  importItem,
  updateImportItem,
  isHistorical = false,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const formRef = React.createRef<HTMLFormElement>()

  function handleOnSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const newImportItem = cloneDeep(importItem)
    newImportItem.fromAdviserEmail = event.currentTarget.advisorEmail.value
    updateImportItem(newImportItem)
    setIsEditing(false)
  }

  function handleInputChange() {
    formRef.current?.requestSubmit()
  }

  function handleInviteToggle() {
    const newImportItem = cloneDeep(importItem)
    newImportItem.inviteFromAdviser = !newImportItem.inviteFromAdviser
    updateImportItem(newImportItem)
  }

  const InviteElement: React.ReactNode = isHistorical ? (
    importItem.inviteFromAdviser ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1" onClick={handleInviteToggle}>
            <div className="flex items-center">
              <LuMail className="text-primary size-3" />
            </div>
            Invite will be sent
            <Switch checked size="sm" className="show-on-row-hover" />
          </div>
        </TooltipTrigger>

        <TooltipContent>
          “{importItem.fromAdviserEmail}” will be invited to join you on RQ.
          It’s free for them to sign up and work with you on RQ.
        </TooltipContent>
      </Tooltip>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2" onClick={handleInviteToggle}>
            <div className="flex items-center gap-1">
              <div className="text-dim hover:text-primary relative inline-block">
                <LuMail className="size-3" />
                <LuSlash className="absolute top-0 left-0 size-3" />
              </div>
              Invite will not be sent
            </div>
            <Switch checked={false} size="sm" className="show-on-row-hover" />
          </div>
        </TooltipTrigger>

        <TooltipContent>
          This referral will be logged against “{importItem.fromAdviserEmail}”,
          but the adviser will not be invited. You can invite them to join you
          later.
        </TooltipContent>
      </Tooltip>
    )
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <LuMail className="text-primary size-3" />
          Invite will be sent
        </span>
      </TooltipTrigger>

      <TooltipContent>
        “{importItem.fromAdviserEmail}” will be invited to join you on RQ. It’s
        free for them to sign up and work with you on RQ.
      </TooltipContent>
    </Tooltip>
  )

  if (isEditing) {
    return (
      <>
        <form ref={formRef} onSubmit={handleOnSubmit}>
          <Input
            placeholder="john.smith@company.com"
            defaultValue={importItem.fromAdviserEmail}
            name="advisorEmail"
            type="email"
            onBlur={handleInputChange}
            required
          />

          {isHistorical && (
            <div className="text-dim mt-1 text-xs">{InviteElement}</div>
          )}

          <span className="mt-1 block text-right">
            <Button type="submit" size="icon-xs" variant="outline-primary">
              <LuCheck />
            </Button>
          </span>
        </form>
      </>
    )
  }

  if (importItem.fromAdviserEmail && importItem.fromAdviserEmailValid) {
    const isInvite = !isHistorical || importItem.inviteFromAdviser

    return (
      <>
        <span
          className="cursor-pointer whitespace-nowrap"
          onClick={() => setIsEditing(true)}
        >
          {isInvite && importItem.importStatus === 'imported' && (
            <LuCircleCheck
              className="text-success mr-1 inline-block -translate-y-px"
            />
          )}

          {isInvite && importItem.importStatus !== 'imported' && (
            <LuCirclePlus
              className="text-info mr-1 inline-block -translate-y-px"
            />
          )}

          {!isInvite && importItem.importStatus === 'imported' && (
            <LuCircleCheck className="mr-1 inline-block -translate-y-px" />
          )}

          {!isInvite && importItem.importStatus !== 'imported' && (
            <LuCircleEllipsis className="mr-1 inline-block -translate-y-px" />
          )}

          {importItem.fromAdviserEmail}

          <span className="show-on-row-hover">
            {', '}
            <span className="text-xs">
              <Button variant="plain" onClick={() => setIsEditing(true)}>
                edit
              </Button>
            </span>
          </span>
        </span>
        <div className="text-dim mt-0.5 text-xs">{InviteElement}</div>
      </>
    )
  }

  if (importItem.fromAdviserEmail) {
    return (
      <>
        <span
          className="text-destructive cursor-pointer"
          onClick={() => setIsEditing(true)}
        >
          <LuTriangleAlert className="mr-1 inline-block -translate-y-px" />
          {importItem.fromAdviserEmail}
        </span>

        <div className="text-destructive mt-1 text-xs">
          Adviser email address is not valid
          <span className="show-on-row-hover">
            ,{' '}
            <Button variant="plain" onClick={() => setIsEditing(true)}>
              edit
            </Button>
          </span>
        </div>
      </>
    )
  }

  return (
    <>
      <span
        className="text-destructive cursor-pointer"
        onClick={() => setIsEditing(true)}
      >
        <LuTriangleAlert className="mr-1 inline-block -translate-y-px" />
        No adviser email address provided
      </span>
      <span className="show-on-row-hover">
        ,{' '}
        <span className="text-xs">
          <Button variant="plain" onClick={() => setIsEditing(true)}>
            edit
          </Button>
        </span>
      </span>
    </>
  )
}

export default InviteNewFromAdviserCell
